/**
 * `MembershipStatus` — the server-derived, BINARY membership classification
 * that drives all membership gating (042-membership-access-binary, D-202,
 * superseding the three-state model of 028-membership-state-gating).
 *
 * Contract: `specs/042-membership-access-binary/contracts/admission-and-capability-contract.md`
 * (supersedes `specs/028-membership-state-gating/contracts/membership-status.md`).
 * Decisions: D-202 (binary access — `valid` = full member, any non-active state
 * = the non-member view only), D-113 (member identity admits-with-membership-
 * state), research Decision 8 (demo signal today, real Kilta signal behind the
 * identity port later).
 *
 * The value is read from the trusted session (`AuthState.member.membershipStatus`),
 * NOT re-derived from a client-only flag (FR-001 / SC-001). The capability layer
 * (`capabilities.ts`) and the route guard consume this single classifier. An
 * ENDED membership (`lapsed` at the keha registry
 * boundary) never reaches the client as a status: it is refused a session at
 * sign-in (auth-callback-telia), so the client only ever holds `'none'` (no
 * member session) or `'valid'`.
 */

/**
 * The two membership states (D-202, binary access). `none` = authenticated but
 * NOT an active member (never a member, or an ended membership that was refused
 * a session); `valid` = current active member. There is no `lapsed` client
 * state: an ended membership is treated exactly like a never-member — the
 * non-member view, no membership features (the former three-state carve-out is
 * removed).
 */
export type MembershipStatus = 'none' | 'valid';

/**
 * The most restricted safe state. When the source cannot produce a
 * classification (the real signal is unavailable, or a member session is
 * missing the field), gating defaults here rather than assuming validity
 * (fail-closed to the 042 admission-and-capability contract's `none` floor).
 * `none` is the least-access state.
 */
export const FAIL_CLOSED_STATUS: MembershipStatus = 'none';
